// client/src/pages/bank-statements/ManageStatements.js

import React, { useState, useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { FaTrash } from 'react-icons/fa';
import useSalaries from '../../hooks/useSalaries';

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (m) => {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1).toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric'
  });
};

const eur = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

function ManageStatements() {
  const { salaries, loading, error, fetchSalaries, saveSalary, removeSalary } = useSalaries();
  const [month, setMonth] = useState(currentMonth());
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSalaries().catch(() => {});
  }, [fetchSalaries]);

  const handleSave = async () => {
    if (amount === '' || Number(amount) < 0) return;
    try {
      await saveSalary(month, Number(amount));
      setAmount('');
      setMessage(`Salaire enregistré pour ${monthLabel(month)}.`);
      await fetchSalaries();
    } catch (err) {
      /* erreur affichée via le hook */
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Supprimer le salaire défini pour ${monthLabel(m)} ?`)) return;
    try {
      await removeSalary(m);
      await fetchSalaries();
    } catch (err) {
      /* erreur affichée via le hook */
    }
  };

  return (
    <div>
      <h1>Gérer</h1>

      {error && <Alert variant="danger">{error}</Alert>}
      {message && (
        <Alert variant="success" dismissible onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Card.Title as="h5">Salaire mensuel</Card.Title>
          <p className="text-muted" style={{ fontSize: 14 }}>
            Définissez le salaire attendu pour un mois. Il sert de revenu minimum dans
            l&apos;Analyse des dépenses — pratique en début de mois, avant que le virement
            n&apos;apparaisse dans le relevé. Dès que le salaire réel dépasse cette valeur,
            c&apos;est le montant réel qui est affiché.
          </p>
          <Row className="g-3 align-items-end">
            <Col sm={4}>
              <Form.Label className="small text-muted mb-1">Mois</Form.Label>
              <Form.Control type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </Col>
            <Col sm={4}>
              <Form.Label className="small text-muted mb-1">Salaire (€)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                placeholder="Ex : 2500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Col>
            <Col sm={4}>
              <Button variant="primary" onClick={handleSave} disabled={amount === ''}>
                Enregistrer
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Card.Title as="h5">Salaires définis</Card.Title>
          {loading ? (
            <Spinner animation="border" />
          ) : salaries.length === 0 ? (
            <p className="text-muted mb-0">Aucun salaire défini pour l&apos;instant.</p>
          ) : (
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Mois</th>
                  <th className="text-end">Salaire</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {salaries.map((s) => (
                  <tr key={s.month}>
                    <td className="text-capitalize">{monthLabel(s.month)}</td>
                    <td className="text-end">{eur(s.amount)}</td>
                    <td className="text-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(s.month)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default ManageStatements;
